import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ConsultationsCard, { ConsultationsCardProps } from '../components/ConsultationsCard'

import { ConsultationStatus } from '@/core/models/consultation.model'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { selectConsultations, updateConsultation } from '@/redux/reducers/consultation.reducer'
import { isStaff } from '@/utils/type-guards'
import { ConsultationData } from '@/pages/consultations/components/ConsultationsTable'

export function ConsultationCardContainer(props: Pick<ConsultationsCardProps, 'sx'>) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [selectedFilter, setSelectedFilter] = useState<string>('today')
  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(5)

  const authUser = useAppSelector(selectAuthUser)
  const consultations = useAppSelector(selectConsultations)

  const consultationsDataByFilter: Record<string, ConsultationData[]> = useMemo(() => {
    if (consultations.length === 0) return { today: [], later: [] }
    const consultationsData = consultations.map((consultation) => ({
      id: consultation._id,
      doctor: consultation.medecin ? _.pick(consultation.medecin.userInfo, ['gender', 'name', 'surname']) : undefined,
      patient: consultation.patient ? _.pick(consultation.patient.userInfo, ['gender', 'name', 'surname']) : { name: '', surname: '' },
      pds: consultation.pds ? _.pick(consultation.pds.userInfo, ['gender', 'name', 'surname']) : undefined,
      showButtons: {
        accept:
          consultation.status === 'scheduled' &&
          authUser?._id !== (typeof consultation.createdBy === 'string' ? consultation.createdBy : consultation.createdBy._id) &&
          !consultation.waitingRoom,
        live: _.some(['scheduled', 'active', 'accepted'], (status) => status === consultation.status),
        refuse:
          consultation.status === 'scheduled' &&
          authUser?._id !== (typeof consultation.createdBy === 'string' ? consultation.createdBy : consultation.createdBy._id) &&
          !consultation.waitingRoom,
      },
      startDate: consultation.startDate,
      status: consultation.waitingRoom && consultation.status === ConsultationStatus.Scheduled ? 'waiting' : consultation.status,
      type: consultation.type as string,
    }))

    const sortedConsultationsData = [...consultationsData].sort((a, b) => dayjs(b.startDate).diff(dayjs(a.startDate)))

    return {
      today: _.filter(sortedConsultationsData, (consultationData) => dayjs(consultationData.startDate).isSame(dayjs(), 'day')),
      later: _.filter(sortedConsultationsData, (consultationData) => dayjs(consultationData.startDate).isAfter(dayjs().endOf('day'))),
    }
  }, [authUser, consultations, selectedFilter])

  const filters = useMemo(
    () =>
      _.map(
        [
          {
            id: 'today',
            title: t('page.home.consultations.selection.today'),
            activeColor: '#269CA6',
          },
          {
            id: 'later',
            title: t('page.home.consultations.selection.later'),
            activeColor: '#FF5C57',
          },
        ],
        (filter) => ({
          ...filter,
          badgeContent: consultationsDataByFilter[filter.id].length,
          selected: selectedFilter === filter.id,
        })
      ),
    [consultationsDataByFilter, selectedFilter, t]
  )

  useEffect(() => {
    // dispatch(
    //   getConsultations(
    //     {
    //       startDate: {
    //         $gte: dayjs().startOf('day').toISOString(),
    //       },
    //     },
    //     {
    //       limit: 0,
    //       order: 'ASC',
    //       orderBy: 'startDate',
    //     }
    //   )
    // )
  }, [dispatch])

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter)
    setPage(1) // Reset to first page when changing filter
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    setPage(1) // Reset to first page when changing rows per page
  }

  let emptyMessage: string
  switch (selectedFilter) {
    case 'today':
      emptyMessage = t('page.home.consultations.noConsultationMessage', { context: 'today' })
      break
    case 'later':
      emptyMessage = t('page.home.consultations.noConsultationMessage', { context: 'later' })
      break
    default:
      emptyMessage = ''
      break
  }

  const handleAcceptConsultation = (consultationId: string) => {
    dispatch(
      updateConsultation(consultationId, {
        status: ConsultationStatus.Accepted,
      })
    )
  }

  const handleRefuseConsultation = (consultationId: string) => {
    dispatch(
      updateConsultation(consultationId, {
        status: ConsultationStatus.Refused,
      })
    )
  }

  const paginatedConsultations = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return consultationsDataByFilter[selectedFilter].slice(startIndex, endIndex)
  }, [consultationsDataByFilter, selectedFilter, page, rowsPerPage])

  return (
    <ConsultationsCard
      {...props}
      consultations={paginatedConsultations}
      onAcceptConsultation={handleAcceptConsultation}
      onRefuseConsultation={handleRefuseConsultation}
      emptyMessage={emptyMessage}
      filters={filters}
      isDoctor={isStaff(authUser) && authUser?.status === 'MEDECIN'}
      onFilterChange={handleFilterChange}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
      totalCount={consultationsDataByFilter[selectedFilter].length}
    />
  )
}

export default ConsultationCardContainer
